import type React from "react";
import { useId } from "react";
import CheckboxControl from "../../primitives/Checkbox";
import { FieldMessages, fieldAccessibility, type LegacyField } from "./LegacyFieldAdapter";

// Redux Form adapters for the single and multiple checkbox fields, extracted from
// inputs.jsx as part of replacing the hand-rolled `.control_indicator` checkbox
// with an owned primitive. inputs.jsx re-exports both, so call sites -- 133 for
// `Checkbox`, 1 for `MultiCheckbox` -- are unchanged.
//
// The adapter owns every Redux Form concern: reading field.input, deriving the
// checked state, translating the primitive's boolean callback back into
// field.input.onChange, and rendering the label and messages through the one
// shared accessibility policy in LegacyFieldAdapter. The primitive underneath
// knows none of that.
//
// Both were class components purely to hold a couple of bound handlers; neither
// used state, lifecycle, or a ref, and no call site passes `withRef` (every
// `withRef` in the repository is on a Select field), so both are now functions.

type CheckboxOption = {
  label: React.ReactNode;
  value: { id: string | number };
};

type CheckboxFieldProps = LegacyField & {
  /**
   * Accepted and deliberately NOT forwarded. Nearly every call site passes
   * `className="app-form-control"`, and the replaced implementation dropped it on
   * the floor -- it never reached the input. Forwarding it now would apply the
   * 34px-high bordered text-input geometry to a 20x20 checkbox. Declared here so
   * the omission reads as a decision rather than an oversight.
   */
  className?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  id?: string;
  input: {
    name?: string;
    onBlur?: (value?: unknown) => void;
    onChange: (value: boolean) => void;
    onFocus?: React.FocusEventHandler<HTMLElement>;
    value?: unknown;
  };
  /** An externally sourced value that wins while Redux Form is still pristine. */
  inputValue?: unknown;
  onInputChange?: (checked: boolean) => void;
  placeholder?: React.ReactNode;
};

type MultiCheckboxFieldProps = LegacyField & {
  className?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  id?: string;
  input: {
    name?: string;
    onBlur?: (value?: unknown) => void;
    onChange: (value: Record<string, boolean>) => void;
    value?: unknown;
  };
  options: CheckboxOption[];
  placeholder?: string;
};

/**
 * The replaced implementation computed this inline and then assigned the result
 * to `field.input.checked` -- writing to a Redux Form prop object as a render
 * side effect. The expression is preserved exactly; only the destination changed.
 *
 * Reading it out: `inputValue` is the caller's own copy of the persisted value and
 * wins while Redux Form is pristine, because a pristine field is `""` rather than
 * `false`. Once the user unticks the box Redux Form holds a literal `false`, which
 * is what `!== false` exists to detect, and from then on the form's value decides.
 */
function isChecked(field: { input: { value?: unknown }; inputValue?: unknown }): boolean {
  return Boolean((field.inputValue && field.input.value !== false) || field.input.value);
}

/** Redux Form yields `""` for a pristine multiple-checkbox field, not an object. */
function selectionMap(value: unknown): Record<string, boolean> {
  return typeof value === "object" && value !== null ? (value as Record<string, boolean>) : {};
}

/**
 * Redux Form names carry spaces — `permissions[users][Section - Access]` — and
 * an id derived from one cannot be referenced by `aria-labelledby`, which is a
 * space-separated ID list: the browser splits on the spaces and resolves none of
 * the fragments.
 *
 * The replaced control was a native input nested in its `<label>`, so it was
 * named implicitly and the spaces never mattered. The owned control is named by
 * reference, so they do. Verified on staging: 58 of 148 checkboxes on /role/new
 * had no accessible name, and exactly those 58 had a whitespace-bearing
 * `aria-labelledby`.
 */
function referenceableId(id: string): string {
  return id.replace(/\s+/g, "-");
}

export function Checkbox(field: CheckboxFieldProps): React.ReactElement {
  const generatedId = useId();
  const inputId = referenceableId(field.id || field.input.name || generatedId);
  const accessibility = fieldAccessibility(field, inputId);
  const checked = isChecked(field);

  return (
    <div className={accessibility.hasError ? "app-validation-error" : undefined}>
      <label className="control control-checkbox" htmlFor={inputId}>
        {!field.hideLabel ? <span className="control-checkbox__label">{field.placeholder}</span> : null}
        <CheckboxControl
          aria-describedby={accessibility.inputProps["aria-describedby"]}
          aria-invalid={accessibility.inputProps["aria-invalid"]}
          aria-required={accessibility.inputProps["aria-required"]}
          checked={checked}
          disabled={field.disabled}
          id={inputId}
          // With the visible text suppressed the wrapping label has nothing to
          // name the control with, so fall back to naming it directly. The
          // replaced implementation left it nameless.
          label={field.hideLabel && typeof field.placeholder === "string" ? field.placeholder : undefined}
          name={field.input.name}
          // A value-bearing Redux Form blur writes that value back into state.
          // Mark the field touched without replaying the value captured before
          // an immediately preceding checkbox change committed.
          onBlur={() => field.input.onBlur?.()}
          onCheckedChange={(next) => {
            field.input.onChange(next);
            if (field.onInputChange) {
              // Deferred by one turn, as before: the callbacks at these call sites
              // read form state, which Redux Form has not committed yet.
              setImmediate(() => field.onInputChange?.(next));
            }
          }}
          onFocus={field.input.onFocus}
        />
      </label>
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}

export function MultiCheckbox(field: MultiCheckboxFieldProps): React.ReactElement {
  const generatedId = useId();
  const inputId = referenceableId(field.id || field.input.name || generatedId);
  const accessibility = fieldAccessibility(field, inputId);
  const selection = selectionMap(field.input.value);

  return (
    <div className={accessibility.hasError ? "app-validation-error" : undefined}>
      {!field.hideLabel && <span className="app-form-label">{field.placeholder}</span>}
      <fieldset className="app-layout-row" aria-label={field.placeholder}>
        {field.options.map((option) => {
          const optionId = `${inputId}-${option.value.id}`;
          return (
            <div className="app-col-sm-4" key={option.value.id}>
              <div className="app-form-group form-material">
                <label className="control control-checkbox" htmlFor={optionId}>
                  {option.label}
                  <CheckboxControl
                    // The error belongs to the set, but ARIA has no dependable way
                    // to announce a group-level message, so every control in the
                    // group points at it -- the same shape a radio group uses.
                    aria-describedby={accessibility.inputProps["aria-describedby"]}
                    aria-invalid={accessibility.inputProps["aria-invalid"]}
                    aria-required={accessibility.inputProps["aria-required"]}
                    checked={Boolean(selection[option.value.id])}
                    disabled={field.disabled}
                    id={optionId}
                    // Do not let blur replay the selection map from the render
                    // before this option's change committed.
                    onBlur={() => field.input.onBlur?.()}
                    onCheckedChange={(next) => {
                      field.input.onChange({ ...selection, [option.value.id]: next });
                    }}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </fieldset>
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}
